import { useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, HelpCircle, SearchX } from "lucide-react";
import * as React from "react";

import type { DihedralProfile } from "@/api/affdo";
import { ApiError } from "@/api/client";
import {
  isViewablePayloadUrl,
  prefetchArtifactPayloadText,
  useArtifactPayloadText,
} from "@/api/payloads";
import type { ScanPointGeometry, TorsionScanGeometryResponse } from "@/api/scan-geometry";
import { useScanGeometry } from "@/api/scan-geometry";
import { ErrorBoundary } from "@/components/layout/ErrorBoundary";
import { EmptyState } from "@/components/layout/EmptyState";
import { Skeleton } from "@/components/ui/skeleton";
import { formatForPayloadName } from "@/components/viz/molecule-structure";
import { useDebouncedValue } from "@/lib/use-debounced-value";
import { shortHash } from "@/lib/hash-display";
import type { GeometryProvenance, ScanPointMark } from "@/lib/scan-geometry";
import {
  classifyGeometryProvenance,
  curveAnnotationRefusal,
  describeUnknownReason,
  nearestScanPoint,
  PROVENANCE_COPY,
  scanPointAtAngle,
  scanPointMarks,
  scrubbablePoints,
} from "@/lib/scan-geometry";

/**
 * The angle scrub, and the structure it drives (VIZ U5.2b).
 *
 * The mechanic the operator asked for — *"the fragment with the torsion being
 * scanned highlighted and how the molecule changes according to the plot"* —
 * is that a torsion profile and a stack of geometries are ONE object seen two
 * ways. This is the half that turns a scan point into a structure, and states
 * what that structure actually is.
 *
 * ## The honesty requirement is the unit, not a detail
 *
 * `TorsionScanResult.scan_geometries` is not uniform. A point the record holds
 * no optimised geometry for has its slot backfilled with the upstream XTB seed,
 * which is *byte-for-byte indistinguishable from a real optimised geometry
 * while being the wrong structure for the energy beside it*. Nothing about the
 * rendered molecule can betray that. Only the flag can, so the flag is on
 * screen whenever the molecule is, and the two states a reader must not trust
 * (seed backfill, unknown provenance) say so in words as well as in the mark.
 *
 * What that backfill means for the ENERGY depends on the `unconverged_handling`
 * policy and so is not said here (#1240): under `interpolate` the energy is a
 * blend of the bracketing neighbours, under `include` it is the point's own
 * non-converged DFT number. This panel has the geometry flag and not the
 * policy, so it states the half it can evidence — the structure shown is a
 * seed, and the energy is not this structure's. `ScanProvenanceNotes` under the
 * chart has `unconverged_handling` in hand and states the rest.
 *
 * The full contract is
 * `docs/source/dev/reference/scan-geometry-provenance.rst`. Two of its rules
 * shape this file and are worth naming here because their consequences are
 * visible:
 *
 * - **`addressable` is not `fetchable`.** The status is decided from the SYNTAX
 *   of the stored URI, with no catalog lookup under the caller's tenant. A 404
 *   from `download_url` is therefore a NORMAL outcome — a missing row, a
 *   payload never registered under that name, an artifact under another tenant
 *   — and it renders as a STATE ("geometry unavailable"), never as an error
 *   banner and never as "no data" (flowstack#923).
 * - **The scrub's axis is the SCAN's own grid, always.** Not the plotted grid.
 *   Under a fit overlay those differ, and driving the scrub off the plot would
 *   mean stepping through angles the scan may not have. Driving it off
 *   `points[]` means every position the user can reach is a point this record
 *   really holds, with an identity (`content_hash`) that certifies it.
 *
 * ## The fit-overlay gap, and what this unit does about it
 *
 * `U5.2a` left one axis open and said so: under a fit overlay
 * `_apply_fit_overlay` replaces `angles_deg` with the FIT artifact's grid, the
 * fit records nothing about the scan it was fit to, and the echoed
 * `content_hash` therefore does not certify the plotted curve. The three
 * options were: refuse to drive the viewer, drive it while stating the limit,
 * or silently join anyway.
 *
 * **Silently joining is out. Refusing the viewer is also wrong, and this file
 * takes the third option with a sharper edge than "state it": it refuses the
 * JOIN rather than the GEOMETRY.**
 *
 * The uncertainty is not about the structures. `content_hash` names exactly
 * which `TorsionScanResult` these points were read from, and every point's
 * provenance flag is that record's own. What is uncertain is whether a mark
 * placed on THIS CURVE describes THAT structure. So the geometry keeps flowing
 * — scrubbed on the scan's own grid, identified by its own hash — and the
 * per-point marks are withheld from the curve, with the reason on screen
 * (`curveAnnotationRefusal`). Refusing the viewer instead would blank it for
 * every FITTED torsion, which is most of a finished run and precisely the set a
 * user came to read, to protect against a pipeline transient that may not be
 * present at all — while leaving the actual uncertain claim (curve ↔ structure)
 * unaddressed either way.
 *
 * ## Fetching: settle first, then one ring
 *
 * D6 ruled progressive fetching. This implements the settled point immediately
 * — the scrub's value is debounced, so dragging the thumb across a 25-point
 * scan issues ZERO requests until it stops — and then prefetches the two
 * immediate neighbours, which is the common next move.
 *
 * The ring is ONE deep on purpose, and that is also why the ruling's
 * cancellation clause needs no machinery here: nothing is in flight during a
 * drag to cancel, and one ring cannot queue a scan's worth of requests behind a
 * stale position. Payload caching is keyed by `download_url` at
 * `staleTime: Infinity`, which is sound because that URL embeds a content hash
 * — never by the `fs-artifact://` URI, which is a location rewritten at every
 * publish/stage hop and excluded from the record's own hash.
 */

/**
 * The lazy boundary, unchanged: `MoleculeViewer` is reached ONLY through
 * `React.lazy`, here as everywhere, so 3Dmol.js stays behind a dynamic chunk
 * that no route can pull in statically (`viewer-lazy-boundary.test.ts` pins
 * both halves).
 */
const MoleculeViewer = React.lazy(() => import("@/components/viz/MoleculeViewer"));

/** Milliseconds the scrub must hold still before a geometry is fetched. */
const SCRUB_SETTLE_MS = 150;

/** How many points either side of the settled one are warmed. See the header. */
const PREFETCH_RING = 1;

/**
 * The viewer's box. Fixed rather than filling: the pane around it scrolls.
 *
 * **It climbs with the window, and the narrow end is the one that matters
 * (VIS/V9).** This panel now sits in a rail BESIDE the curve rather than under
 * it, which is what makes its height a correctness question instead of a taste
 * one: the pairing is only a pairing while both halves are on screen at once,
 * and at 1024×768 — the narrowest viewport the unit is verified at — a flat
 * 320px box put the molecule's lower third under the fold. 240px clears it with
 * room to spare there, and the full 320px returns at `2xl` where the window has
 * the height for it. Measured, not guessed:
 * `tests/e2e/structure_beside_curve.spec.ts` prints the boxes it asserts on.
 */
const VIEWER_HEIGHT_CLASS = "h-[240px] xl:h-[280px] 2xl:h-[320px]";

export interface ScanGeometryScrub {
  /** Per-plotted-angle provenance for the chart, or `undefined` when this
   * client may not annotate the curve. NEVER an empty array in the refused
   * case — the chart must not be able to tell "no marks" from "no reason to
   * mark", so the two are different types of answer. */
  marks: ScanPointMark[] | undefined;
  /** The scrubbed angle on the SCAN's grid, or `null` before the first settle. */
  selectedAngleDeg: number | null;
  selectAngle: (angleDeg: number) => void;
  /** Spread into {@link ScanGeometryPanel}. */
  panel: ScanGeometryPanelProps;
}

/**
 * Wire one torsion's scan geometry to a scrub.
 *
 * Returns DATA, never JSX, so both surfaces that need it — the live run
 * workspace and the Analysis tab — drive the same panel from the same state
 * without either owning the other's layout (VIZ U5.4's reuse rule).
 *
 * `curveScanHashes` is the caller's answer to "which `TorsionScanResult`s could
 * the curve on screen have been drawn from?", because only the caller knows
 * where its curve came from. The check itself lives HERE rather than at each
 * call site, so both surfaces compare the same term the same way — and the term
 * is `content_hash`, never `torsion_scan_result_id`, which is producer-assigned
 * and repeats across re-runs of one torsion.
 *
 * - The **Analysis tab** passes the materialised report's
 *   `input_artifact_hashes`: that report was built from exactly those records,
 *   so membership is a real confirmation.
 * - The **live workspace** passes nothing. `/profiles` does not echo the scan
 *   identity, and widening its body is a separate unit's call — its bytes are
 *   pinned by the U0.4 byte-identity fixtures. `undefined` means "did not
 *   check", which is treated as unconfirmed; an EMPTY array would mean
 *   "checked, and it is not one of these", and both withhold the marks.
 */
export function useScanGeometryScrub({
  runId,
  fragmentId,
  torsionId,
  profile,
  refreshKey,
  curveScanHashes,
  enabled = true,
}: {
  runId: string;
  fragmentId: string | null;
  torsionId: string | null;
  profile: DihedralProfile | undefined;
  refreshKey?: string;
  curveScanHashes?: readonly string[];
  enabled?: boolean;
}): ScanGeometryScrub {
  const query = useScanGeometry(runId, fragmentId ?? "", torsionId ?? "", {
    enabled: enabled && fragmentId !== null && torsionId !== null,
    refreshKey,
  });
  const scan = query.data;

  const [picked, setPicked] = React.useState<number | null>(null);
  // The pick is cleared when the ADDRESS changes, not when the data does: a
  // mid-run refetch of the same torsion must not throw the user back to point
  // zero, while moving to another torsion (or fragment) must, since an angle
  // carried across is an angle in a different scan.
  React.useEffect(() => {
    setPicked(null);
  }, [runId, fragmentId, torsionId]);

  const points = scrubbablePoints(scan);
  // Default to the FIRST point rather than to nothing: a scrub whose viewer is
  // empty until touched reads as "this torsion has no geometry", which is the
  // conflation flowstack#923 forbids.
  const selectedAngleDeg = picked ?? points[0]?.angle_deg ?? null;

  const curveIsFromThisScan =
    curveScanHashes === undefined || scan === undefined
      ? undefined
      : curveScanHashes.includes(scan.content_hash);
  const refusal = curveAnnotationRefusal(profile, scan, curveIsFromThisScan);
  const marks =
    profile !== undefined && scan !== undefined && refusal === null
      ? scanPointMarks(profile, scrubbablePoints(scan))
      : undefined;

  return {
    marks,
    selectedAngleDeg,
    selectAngle: setPicked,
    panel: {
      scan,
      isLoading: query.isLoading,
      error: query.isError ? query.error : null,
      refusal,
      selectedAngleDeg,
      onSelectAngle: setPicked,
    },
  };
}

/** What this panel calls itself, wherever it is drawn. */
export const SCAN_GEOMETRY_HEADING = "Structure at this angle";

export interface ScanGeometryPanelProps {
  scan: TorsionScanGeometryResponse | undefined;
  isLoading: boolean;
  error: unknown;
  /** Why the curve beside this carries no provenance marks, or `null`. */
  refusal: string | null;
  selectedAngleDeg: number | null;
  onSelectAngle: (angleDeg: number) => void;
  /**
   * `false` when a CONTAINER already prints this panel's name — the run
   * workspace's rail, where each section is a `VisualPanel` whose header
   * carries the same words (VIS/V9). The heading then goes `sr-only` rather
   * than away: it is the accessible name of both the region and the scrub
   * (`aria-labelledby`), so deleting the element would leave the slider
   * unnamed to answer a question about ink.
   *
   * Defaults to `true` — the Analysis tab draws this in a plain column with
   * nothing else naming it.
   */
  showHeading?: boolean;
}

export function ScanGeometryPanel({
  scan,
  isLoading,
  error,
  refusal,
  selectedAngleDeg,
  onSelectAngle,
  showHeading = true,
}: ScanGeometryPanelProps): JSX.Element {
  const headingId = React.useId();

  if (isLoading) {
    return (
      <Section headingId={headingId} showHeading={showHeading}>
        <Skeleton className={`w-full ${VIEWER_HEIGHT_CLASS}`} />
      </Section>
    );
  }

  if (error) {
    // The 404 is a CLAIM, not a failure: this view addresses one specific
    // object, so `torsion_scan_not_found` says the run published no
    // TorsionScanResult here (or this deployment has no catalog wired). The
    // route returns it rather than an empty body precisely so it cannot be read
    // as "this scan recorded no geometries" — so neither may this panel.
    const notFound = error instanceof ApiError && error.errorCode === "torsion_scan_not_found";
    return (
      <Section headingId={headingId} showHeading={showHeading}>
        <EmptyState
          title={
            notFound ? "No scan record for this torsion." : "Could not read the scan geometry."
          }
          description={
            notFound
              ? "This run published no TorsionScanResult at this address, so there are no per-point geometries to step through. The curve above is drawn from whatever this run did publish."
              : error instanceof Error
                ? error.message
                : "Unknown error."
          }
        />
      </Section>
    );
  }

  if (scan === undefined) {
    return (
      <Section headingId={headingId} showHeading={showHeading}>
        <EmptyState
          title="Could not read the scan geometry."
          description="The server returned no body for this torsion."
        />
      </Section>
    );
  }

  if (!Array.isArray(scan.points)) {
    // A trust-boundary guard, not paranoia: this is server-response data, and
    // "the body was not the shape I expected" must not fall through to the
    // empty-grid state below, which is a CLAIM about the science ("this scan
    // recorded no points") that a malformed body does not support.
    return (
      <Section headingId={headingId} showHeading={showHeading}>
        <EmptyState
          title="Could not read the scan geometry."
          description="The server returned a body without a per-point list."
        />
      </Section>
    );
  }

  const points = scrubbablePoints(scan);
  if (points.length === 0) {
    return (
      <Section headingId={headingId} showHeading={showHeading}>
        <EmptyState
          title="This scan recorded no points."
          description="The record resolved, but its grid is empty — there is nothing to step through."
        />
        <ScanIdentity scan={scan} />
      </Section>
    );
  }

  // Resolved through `scanPointAtAngle`, not `===`, so the SAME wrap and
  // tolerance rule that decides the curve's marks decides the selection. A
  // click on a datum plotted at 360° must reach the scan's 0° point; under
  // `===` it silently fell back to point 0 instead — which happened to be that
  // point here and would not be on a grid whose seam sits elsewhere.
  //
  // A selected angle the scan has NO point for is its own state, not a fall
  // back to point 0 — see {@link ScanAngleMiss}. `selectedAngleDeg === null` is
  // a different fact again: nothing has been picked yet, which is a default and
  // opens on the first point, with the scrub saying exactly that.
  const selected = selectedAngleDeg === null ? null : scanPointAtAngle(points, selectedAngleDeg);
  if (selectedAngleDeg !== null && selected === null) {
    return (
      <Section headingId={headingId} showHeading={showHeading}>
        <CurveRefusalNote refusal={refusal} />
        <ScanAngleMiss points={points} angleDeg={selectedAngleDeg} onSelectAngle={onSelectAngle} />
        <ScanIdentity scan={scan} />
      </Section>
    );
  }
  const index = selected === null ? 0 : points.indexOf(selected);

  return (
    <Section headingId={headingId} showHeading={showHeading}>
      <CurveRefusalNote refusal={refusal} />
      {!scan.provenance_available && (
        <p
          className="rounded-md border border-border-subtle bg-surface-2 px-2 py-1 text-[11px] leading-snug text-muted-foreground"
          data-testid="scan-provenance-unavailable"
        >
          This record carries no usable provenance list, so every point below reads as unknown — not
          as optimised.
        </p>
      )}
      <ScanScrubControl
        points={points}
        index={index}
        onSelectAngle={onSelectAngle}
        headingId={headingId}
      />
      <ScanPointProvenanceLine point={points[index]} />
      <ScanPointStructure points={points} index={index} torsionId={scan.torsion_id} />
      <ScanIdentity scan={scan} />
    </Section>
  );
}

/** Why the curve above carries no provenance marks. Shared by both selection
 * states, because a refusal is a fact about the CURVE and holds whether or not
 * the selected angle resolved to a point. */
function CurveRefusalNote({ refusal }: { refusal: string | null }): JSX.Element | null {
  if (refusal === null) return null;
  return (
    <p
      className="rounded-md border border-border-subtle bg-surface-2 px-2 py-1 text-[11px] leading-snug text-muted-foreground"
      data-testid="scan-curve-refusal"
    >
      {refusal}
    </p>
  );
}

/**
 * The viewer's `no_record`: the selected angle names no point in this scan.
 *
 * **Replaces a fall-back to point 0** (flowstack#1196, Rupert advisory on
 * PR-1193). That fallback rendered the FIRST point's molecule with nothing
 * saying it was not the selected angle's structure — the exact silent wrong
 * render the chart in the same PR refuses for itself via `no_record`, on the
 * surface where it costs more, because it changes the molecule the reader is
 * looking at. It is reachable: the plot's click handler hands back a **plotted**
 * angle, and under a fit overlay the plotted grid is the fit artifact's, which
 * the scan need not have a point on.
 *
 * ## Two causes, and the second one used to be told as the first
 *
 * flowstack#1202. A selected angle can fail to resolve for two unrelated
 * reasons, and the first cut of this state said the same sentence for both:
 *
 * - **`angle_missing`** — a real angle the grid does not hold. The blame is on
 *   the JOIN, and "this scan's grid holds no point here" is exactly right.
 * - **`angle_unusable`** — the selection is not a usable angle at all (`NaN`,
 *   `±Infinity`). Nothing is near it, no grid holds it, and no re-scan would
 *   change that. Saying "this scan's grid holds no point here" blames the
 *   record for a malformed selection, and a reader chasing that sentence goes
 *   looking at their data instead of at the view.
 *
 * ## Always a way out
 *
 * The scrub is not rendered here — its position would have to be a lie — so a
 * button carries the recovery. **Every miss offers one**, which is the other
 * half of #1202: the non-finite case used to render the sentence and nothing
 * else, and a state a user can only leave by reloading is a dead end, on the
 * one path where the selection may have arrived from a chart click they cannot
 * take back.
 *
 * What is offered differs with the cause, because the honest move differs.
 * `angle_missing` offers the NEAREST recorded point; `angle_unusable` offers
 * the scan's FIRST point — the panel's own default, where a fresh selection
 * opens — since "nearest" is meaningless for an angle nothing can be near. In
 * both cases the offered angle is NAMED and the reader chooses it:
 * {@link nearestScanPoint} is deliberately not wired into the join, because a
 * substitution made on the reader's behalf is the silent wrong render again.
 */
function ScanAngleMiss({
  points,
  angleDeg,
  onSelectAngle,
}: {
  points: readonly ScanPointGeometry[];
  angleDeg: number;
  onSelectAngle: (angleDeg: number) => void;
}): JSX.Element {
  const usable = Number.isFinite(angleDeg);
  // `nearestScanPoint` returns `null` for a non-finite angle by design — see
  // its own note — so the fall-back is the scan's first angled point rather
  // than a relaxed join.
  //
  // **The button always renders**, and that is an invariant rather than a hope
  // (Rupert PR-1227 r0): `points` reaches this component only from
  // `scrubbablePoints`, whose whole job is to drop every point the record gave
  // no angle, and the caller returns the empty-grid state before this branch
  // when the result is empty. So a non-empty `points` has a first angled member
  // by construction. The `find` and the `?? null` exist because the TYPE does
  // not carry that filter, not because the case is reachable — if it ever
  // became reachable, this state would be a dead end again.
  const nearest = usable ? nearestScanPoint(points, angleDeg) : null;
  const recovery = nearest ?? points.find((p) => p.angle_deg !== null) ?? null;
  const recoveryAngle = recovery?.angle_deg ?? null;
  return (
    // `role="status"`, like the provenance line this replaces: arriving at a
    // miss is the same kind of event as arriving at a point, and a reader who
    // does not see the panel must be told either way — including the way out,
    // which is inside the region for that reason.
    <div role="status" className="flex flex-col gap-2" data-testid="scan-angle-no-record">
      <StructureNote
        miss={usable ? "angle_missing" : "angle_unusable"}
        testId="scan-angle-no-record-note"
      >
        <span className="flex flex-col gap-1">
          <span className="font-medium">
            {usable
              ? `No scan point at ${formatAngle(angleDeg)}.`
              : "The selected angle is not a usable angle."}
          </span>
          <span>
            {usable
              ? "This scan's grid holds no point here by any spelling of the angle, so there is no structure to show. Another point's molecule would not be this angle's."
              : "The selection did not arrive as a finite number of degrees, so it names no point on any grid — this scan's grid is not what is missing. Nothing can be nearest to it either."}
          </span>
        </span>
      </StructureNote>
      {recoveryAngle !== null && (
        <button
          type="button"
          onClick={() => onSelectAngle(recoveryAngle)}
          className="self-start rounded-md border border-border-subtle bg-surface-2 px-2 py-1 text-[11px] leading-snug text-foreground ring-offset-background transition-colors hover:bg-surface-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
          data-testid="scan-angle-recover"
          data-recovery={nearest !== null ? "nearest" : "first"}
        >
          {nearest !== null
            ? `Go to the nearest recorded point (${formatAngle(recoveryAngle)})`
            : `Go to this scan's first recorded point (${formatAngle(recoveryAngle)})`}
        </button>
      )}
    </div>
  );
}

function Section({
  headingId,
  showHeading,
  children,
}: {
  headingId: string;
  showHeading: boolean;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <section
      className="flex flex-col gap-2"
      aria-labelledby={headingId}
      data-testid="scan-geometry-panel"
    >
      <h3 id={headingId} className={showHeading ? "label-micro text-muted-foreground" : "sr-only"}>
        {SCAN_GEOMETRY_HEADING}
      </h3>
      {children}
    </section>
  );
}

/**
 * The scrub itself — a range input, and here that IS the right affordance.
 *
 * U5.1 shipped sliders for the torsion and fragment axes and the operator's
 * report was *"these sliders are horrible … by slider i meant buttons"*. That
 * verdict stands and is not contradicted here, because it was about a handful
 * of NAMED things: a range input is a stepper, so reaching the third of five
 * torsions meant passing through the second, and the set was invisible until
 * you had swept it.
 *
 * A scan grid is the opposite kind of axis. Its ~25 members are ordered
 * positions on a continuum, not names; there is no "jump to 195°" the user
 * thinks in, the interesting move is *step and watch*, and a row of 25 pills
 * would be the control U5.1's own `MAX_PILLS` threshold degrades away from.
 * Stepping being cheap is the feature, which is exactly what a slider is for —
 * and it comes with arrow keys, Home/End and a real `aria-valuetext` for free.
 */
function ScanScrubControl({
  points,
  index,
  onSelectAngle,
  headingId,
}: {
  points: readonly ScanPointGeometry[];
  index: number;
  onSelectAngle: (angleDeg: number) => void;
  headingId: string;
}): JSX.Element {
  const point = points[index];
  const angle = point.angle_deg ?? 0;
  return (
    <div className="flex flex-col gap-1">
      <input
        type="range"
        min={0}
        max={points.length - 1}
        step={1}
        value={index}
        aria-labelledby={headingId}
        // The VALUE a screen reader announces is the angle, not the array
        // position: the position is an implementation detail of the control and
        // the angle is the thing the user is choosing.
        aria-valuetext={`${formatAngle(angle)}, point ${index + 1} of ${points.length}`}
        onChange={(event) => {
          const next = points[Number(event.target.value)];
          if (next?.angle_deg !== null && next?.angle_deg !== undefined)
            onSelectAngle(next.angle_deg);
        }}
        className="w-full accent-cyan"
        data-testid="scan-angle-scrub"
      />
      <p className="flex items-baseline justify-between font-mono text-[11px] text-muted-foreground">
        <span data-testid="scan-angle-readout">{formatAngle(angle)}</span>
        <span>
          point {index + 1} of {points.length}
        </span>
      </p>
    </div>
  );
}

function formatAngle(angleDeg: number): string {
  // One decimal only when the grid needs it — a 15° production grid should not
  // read "15.0°", and a shifted grid must not be rounded into looking regular.
  return `${Number.isInteger(angleDeg) ? angleDeg : angleDeg.toFixed(1)}°`;
}

/**
 * What this point's geometry IS, in words, beside the molecule.
 *
 * `seed_fallback` and `unknown` are the two a reader must not trust, and both
 * carry their sentence unconditionally — never behind a hover or a tooltip. The
 * mark on the curve is a glance-level cue; this is the statement.
 */
function ScanPointProvenanceLine({ point }: { point: ScanPointGeometry }): JSX.Element {
  const provenance = classifyGeometryProvenance(point);
  const copy = PROVENANCE_COPY[provenance];
  const reason = describeUnknownReason(point);
  return (
    <div
      role="status"
      className={`flex flex-col gap-0.5 rounded-md border px-2 py-1 text-[11px] leading-snug ${PROVENANCE_TONE[provenance]}`}
      data-testid="scan-point-provenance"
      data-provenance={provenance}
    >
      <span className="font-medium">{copy.label}</span>
      <span className="text-muted-foreground">
        {copy.detail}
        {reason !== null && ` (${reason})`}
      </span>
    </div>
  );
}

/**
 * The tone each reading wears.
 *
 * A `Record` so a reading added to `GeometryProvenance` without deciding its
 * tone is a type error rather than an unstyled box. Colour is the LAST cue
 * here, never the only one: the mark's shape, the label and this sentence all
 * carry the state first.
 */
const PROVENANCE_TONE: Record<GeometryProvenance, string> = {
  optimized: "border-border-subtle bg-surface-2 text-foreground",
  seed_fallback:
    "border-state-failed-validation-border/60 bg-state-failed-validation-bg/50 text-state-failed-validation-fg",
  unknown: "border-border-subtle bg-surface-2 text-foreground",
};

/**
 * The molecule for the settled point.
 *
 * Two rules this component exists to keep:
 *
 * - **Never show the previous point's frame.** The payload query is keyed by
 *   `download_url` with no `placeholderData`, so moving to a point whose bytes
 *   are not cached yields `undefined` rather than the last point's text; and
 *   while the scrub has not settled, the box says so instead of holding a frame
 *   the header no longer describes.
 * - **A 404 is a state.** `addressable` is a claim about URI syntax, not about
 *   this tenant's catalog, so the fetch failing is expected traffic. It renders
 *   as "geometry unavailable" — distinct from "geometry not recorded", which is
 *   a fact about the RECORD.
 */
function ScanPointStructure({
  points,
  index,
  torsionId,
}: {
  points: readonly ScanPointGeometry[];
  index: number;
  torsionId: string;
}): JSX.Element {
  const client = useQueryClient();
  const settledIndex = useDebouncedValue(index, SCRUB_SETTLE_MS);
  // Clamp: `points` can shrink under a mid-run refetch between the pick and the
  // settle, and a stale index would read past the end.
  const settled = points[Math.min(settledIndex, points.length - 1)] ?? points[0];
  const settledRef = settled.geometry;
  const settledStatus = settled.geometry_status;
  const pending = settledIndex !== index;

  // The neighbour ring — see the file header for why one deep, and why this
  // needs no cancellation.
  React.useEffect(() => {
    if (pending) return;
    for (let offset = -PREFETCH_RING; offset <= PREFETCH_RING; offset += 1) {
      if (offset === 0) continue;
      const neighbour = points[settledIndex + offset];
      const url = neighbour?.geometry?.download_url;
      if (url !== undefined) prefetchArtifactPayloadText(client, url);
    }
  }, [client, points, settledIndex, pending]);

  const format = settledRef === null ? null : formatForPayloadName(settledRef.payload_name);
  // `download_url` is server-response data about to be fetched WITH THE USER'S
  // credentials, so it is pinned to the API's own payload-route shape before
  // the fetch rather than after it. `apiFetchPayloadText` enforces the same
  // rule at the boundary and is what actually protects the request; asking here
  // as well is what lets a refused address render as its own state instead of
  // as a generic load failure. This PR declines to trust the server's
  // `geometry_source` vocabulary — a URL gets the same standard.
  const fetchable = settledRef !== null && isViewablePayloadUrl(settledRef.download_url);
  const {
    data,
    isLoading: textLoading,
    error,
  } = useArtifactPayloadText(
    settledStatus === "addressable" && settledRef !== null && format !== null && fetchable
      ? settledRef.download_url
      : null
  );

  if (settledStatus === "not_recorded") {
    return (
      <StructureNote miss="not_recorded" testId="scan-geometry-not-recorded">
        Geometry not recorded for this point.
      </StructureNote>
    );
  }
  if (settledStatus !== "addressable") {
    // `unresolvable`, or a status this release does not know. Both mean the
    // same to a reader — there is an entry and this API cannot turn it into an
    // address — and that is a DIFFERENT fact from "there is no geometry".
    return (
      <StructureNote miss="unresolvable" testId="scan-geometry-unresolvable">
        A geometry is recorded for this point, but it is not an address this API can resolve.
      </StructureNote>
    );
  }
  if (settledRef === null || format === null) {
    return (
      <StructureNote
        miss={settledRef === null ? "unaddressed" : "unparsable"}
        testId="scan-geometry-unreadable"
      >
        {settledRef === null
          ? "This point is addressable but carries no address."
          : `This release cannot parse ${settledRef.payload_name} as a structure.`}
      </StructureNote>
    );
  }
  if (!fetchable) {
    // A state, not an error banner and not "no data": the record names an
    // address, and this client declined to fetch it because it is not the
    // API's own payload route. Said plainly, because the alternative to
    // saying it is a silent blank.
    return (
      <StructureNote miss="unsafe_address" testId="scan-geometry-unsafe-address">
        This point&apos;s address is not one this app will fetch: it is not this API&apos;s payload
        route.
      </StructureNote>
    );
  }
  if (pending || textLoading) {
    return (
      <Skeleton className={`w-full ${VIEWER_HEIGHT_CLASS}`} data-testid="scan-geometry-pending" />
    );
  }
  if (error) {
    // Three different facts, and since flowstack#1202 they are three different
    // states rather than three sentences in one box: a documented size cap and
    // a 404 are things this client DECIDED or was told, while anything else is
    // a load that broke — and only the last of those is a failure to mark as
    // one.
    const tooLarge = error instanceof ApiError && error.errorCode === "payload_too_large";
    const missing = error instanceof ApiError && error.status === 404;
    if (tooLarge) {
      return (
        <StructureNote miss="too_large" testId="scan-geometry-too-large">
          {settledRef.payload_name} is too large to render in the browser.
        </StructureNote>
      );
    }
    if (missing) {
      return (
        // NOT an error, and NOT "no data" — see the component note.
        <StructureNote miss="unavailable" testId="scan-geometry-unavailable">
          Geometry unavailable: this scan point names a payload the catalog does not hold for you.
        </StructureNote>
      );
    }
    return (
      <StructureNote miss="load_failed" testId="scan-geometry-load-failed">
        Could not load {settledRef.payload_name}.
      </StructureNote>
    );
  }
  if (data === undefined || data.trim() === "") {
    return (
      <StructureNote miss="empty_payload" testId="scan-geometry-empty">
        {settledRef.payload_name} is empty, so there is nothing to render.
      </StructureNote>
    );
  }

  return (
    <ErrorBoundary
      fallback={() => (
        <StructureNote miss="unrenderable" testId="scan-geometry-unrenderable">
          {settledRef.payload_name} could not be rendered as a 3D structure.
        </StructureNote>
      )}
    >
      <React.Suspense fallback={<Skeleton className={`w-full ${VIEWER_HEIGHT_CLASS}`} />}>
        <MoleculeViewer
          // Keyed by the ADDRESS so a new point remounts the canvas rather than
          // re-posing the old one — and so nothing about the previous frame can
          // survive into a point whose bytes have not arrived.
          key={settledRef.download_url}
          structureText={data}
          format={format}
          label={`3D structure at scan point ${settledIndex + 1} of torsion ${torsionId}`}
          className={`relative w-full overflow-hidden rounded-md border ${VIEWER_HEIGHT_CLASS}`}
        />
      </React.Suspense>
    </ErrorBoundary>
  );
}

/**
 * Every reason this panel shows words where a structure would be — closed, so
 * a reason added without deciding how it is DRAWN is a type error.
 *
 * The list is long because the reasons are genuinely different, and
 * flowstack#1202 is what happens when that is expressed in copy alone: the
 * "no scan point at this angle" state drew the same box as "geometry not
 * recorded", "not an address I can resolve" and "an address I will not fetch",
 * so a reader scanning the panel saw one state four times. The chart got four
 * marks for exactly this reason — two marks cannot carry three meanings — and
 * the viewer must not collapse them back.
 */
type StructureMiss =
  | "angle_missing"
  | "angle_unusable"
  | "not_recorded"
  | "unresolvable"
  | "unaddressed"
  | "unparsable"
  | "unsafe_address"
  | "unavailable"
  | "too_large"
  | "empty_payload"
  | "load_failed"
  | "unrenderable";

/**
 * WHOSE answer is missing — the channel a reader reads before the sentence.
 *
 * Four families rather than twelve marks, because the distinction that has to
 * survive a glance is not which of five addressing faults occurred; it is
 * whether the gap is in what you ASKED for, in what the record HOLDS, in what
 * this client can READ, or in something that went wrong. The sentence carries
 * the specific cause from there.
 *
 * - `selection` — the angle on screen names no point in this record. The only
 *   family that offers a MOVE, because it is the only one the reader can fix.
 * - `absent` — the record legitimately holds nothing here.
 * - `unreadable` — something IS recorded and this client will not turn it into
 *   a structure: it cannot resolve it, cannot parse it, will not fetch it, or
 *   the catalog does not hold it for this caller. Nothing failed.
 * - `failure` — it exists and the attempt to show it broke.
 */
type StructureMissFamily = "selection" | "absent" | "unreadable" | "failure";

const MISS_FAMILY: Record<StructureMiss, StructureMissFamily> = {
  angle_missing: "selection",
  angle_unusable: "selection",
  not_recorded: "absent",
  empty_payload: "absent",
  unresolvable: "unreadable",
  unaddressed: "unreadable",
  unparsable: "unreadable",
  // A refused address and a cap are DECISIONS this client made, not failures —
  // same shape as "cannot parse it", so the same mark.
  unsafe_address: "unreadable",
  too_large: "unreadable",
  // The 404, which `addressable` never promised against: a state, never an
  // error banner and never "no data" (see the component note).
  unavailable: "unreadable",
  load_failed: "failure",
  unrenderable: "failure",
};

/**
 * How each family is drawn. The vocabulary is `VisualPanelNotice`'s
 * deliberately — dashed ground means "nothing here", solid means "something is
 * here and this is what we know about it" — so that when `ScanGeometryPanel`
 * adopts the scientific-visual container (a separate change, per
 * `docs/source/dev/reference/webapp-visual-panel.rst`) these states map onto
 * `empty` / `unknown` / `error` without re-deciding anything. `selection` is
 * the one family with no container equivalent, and it carries the distinct
 * glyph rather than a hue: an angle the record does not hold is not a severity.
 */
const MISS_MARK: Record<
  StructureMissFamily,
  { ground: string; icon: React.ComponentType<{ className?: string }> | null }
> = {
  selection: {
    ground: "border-border-subtle bg-surface-2 text-muted-foreground",
    icon: SearchX,
  },
  absent: {
    ground: "border-dashed border-border-subtle bg-surface-2 text-muted-foreground",
    icon: null,
  },
  unreadable: {
    ground: "border-border-subtle bg-surface-2 text-muted-foreground",
    icon: HelpCircle,
  },
  failure: {
    ground:
      "border-state-failed-system-border bg-state-failed-system-bg text-state-failed-system-fg",
    icon: AlertTriangle,
  },
};

function StructureNote({
  miss,
  testId,
  children,
}: {
  miss: StructureMiss;
  testId: string;
  children: React.ReactNode;
}): JSX.Element {
  const { ground, icon: Icon } = MISS_MARK[MISS_FAMILY[miss]];
  return (
    <div
      className={`flex w-full flex-col items-center justify-center gap-2 rounded-md border px-3 text-center text-[11px] leading-snug ${ground} ${VIEWER_HEIGHT_CLASS}`}
      data-testid={testId}
      // The cause, on the element, so a test asserts the state this panel
      // actually drew rather than a sentence somebody could reword.
      data-miss={miss}
      data-miss-family={MISS_FAMILY[miss]}
    >
      {Icon !== null && <Icon className="h-4 w-4 shrink-0" aria-hidden />}
      {children}
    </div>
  );
}

/**
 * WHICH scan these points came from.
 *
 * Shown rather than kept internal because an angle is identical across every
 * re-run of a torsion: two scans of one torsion join cleanly on every angle
 * while being different science. `content_hash` is the discriminating term and
 * is printed; `torsion_scan_result_id` is producer-assigned and repeats across
 * re-runs, so it is deliberately NOT the thing a reader is invited to compare.
 */
function ScanIdentity({ scan }: { scan: TorsionScanGeometryResponse }): JSX.Element {
  return (
    <p
      className="flex flex-wrap items-baseline gap-x-2 font-mono text-[10px] text-muted-foreground"
      data-testid="scan-identity"
    >
      <span>{scan.method ?? "method unrecorded"}</span>
      <span title={scan.content_hash}>{shortHash(scan.content_hash)}</span>
      <span>{scan.created_at}</span>
    </p>
  );
}
